const BACKEND_URI = "";

import { ChatRequest, HistoryRequest, Conversation, ChatMessage, CosmosDBHealth, CosmosDBStatus, Assistant, FeedbackRequest } from "./models";

function getHeaders(userCookie?: string, token?: string): Record<string, string> {
    const headers: Record<string, string> = {
        "Content-Type": "application/json"
    };

    if (userCookie) {
        headers["Cookie"] = `user=${userCookie}`;
    }

    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }

    return headers;
}

export async function chatApi(request: ChatRequest, token: string | undefined): Promise<Response> {
    return await fetch(`${BACKEND_URI}/chat`, {
        method: "POST",
        headers: getHeaders(undefined, token),
        body: JSON.stringify(request)
    });
}

export function getCitationFilePath(citation: string): string {
    const url = `${BACKEND_URI}/content/${citation}`;
    return url;
}

export async function generateHistory(request: HistoryRequest): Promise<Response> {
    return await fetch(`${BACKEND_URI}/history/generate`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify(request)
    });
}

export const historyList = async (userCookie: string): Promise<Conversation[] | null> => {
    const response = await fetch("/history/list", {
        method: "GET",
        headers: getHeaders(userCookie)
    })
        .then(async res => {
            const payload = await res.json();
            if (!Array.isArray(payload)) {
                console.error("There was an issue fetching your data.");
                return null;
            }
            const conversations: Conversation[] = await Promise.all(
                payload.map(async (conv: any) => {
                    let convMessages: ChatMessage[] = [];
                    convMessages = await historyRead(conv.id)
                        .then(res => {
                            return res;
                        })
                        .catch(err => {
                            console.error("error fetching messages: ", err);
                            return [];
                        });
                    const conversation: Conversation = {
                        id: conv.id,
                        assistant: conv.assistant,
                        title: conv.title,
                        messages: convMessages,
                        date: conv.updatedAt
                    };
                    return conversation;
                })
            );
            return conversations;
        })
        .catch(err => {
            console.error("There was an issue fetching your data.");
            return null;
        });

    return response;
};

export const historyRead = async (convId: string): Promise<ChatMessage[]> => {
    const response = await fetch("/history/read", {
        method: "POST",
        body: JSON.stringify({
            conversation_id: convId
        }),
        headers: getHeaders()
    })
        .then(async res => {
            if (!res) {
                return [];
            }
            const payload = await res.json();
            let messages: ChatMessage[] = [];
            if (payload?.chat_messages) {
                payload.chat_messages.forEach((msg: any) => {
                    const message: ChatMessage = {
                        id: msg.id,
                        message: msg.message,
                        context: msg.context,
                        date: msg.createdAt,
                        feedback: msg.feedback,
                        feedback_comment: msg.feedbackComment
                    };
                    messages.push(message);
                });
            }
            return messages;
        })
        .catch(err => {
            console.error("There was an issue fetching your data.");
            return [];
        });
    return response;
};

export const historyEnsure = async (): Promise<CosmosDBHealth> => {
    const response = await fetch("/history/ensure", {
        method: "GET",
        headers: getHeaders()
    })
        .then(async res => {
            let respJson = await res.json();
            let formattedResponse;
            if (respJson.message) {
                formattedResponse = CosmosDBStatus.Working;
            } else {
                if (res.status === 500) {
                    formattedResponse = CosmosDBStatus.NotWorking;
                } else {
                    formattedResponse = CosmosDBStatus.NotConfigured;
                }
            }
            if (!res.ok) {
                return {
                    cosmosDB: false,
                    status: formattedResponse
                };
            } else {
                return {
                    cosmosDB: true,
                    status: formattedResponse
                };
            }
        })
        .catch(err => {
            console.error("There was an issue fetching your data.");
            return {
                cosmosDB: false,
                status: err
            };
        });
    return response;
};

export const assistantsEnsure = async (): Promise<CosmosDBHealth> => {
    const response = await fetch("/assistants/ensure", {
        method: "GET",
        headers: getHeaders()
    })
        .then(async res => {
            let respJson = await res.json();
            let formattedResponse;
            if (respJson.message) {
                formattedResponse = CosmosDBStatus.Working;
            } else {
                if (res.status === 500) {
                    formattedResponse = CosmosDBStatus.NotWorking;
                } else {
                    formattedResponse = CosmosDBStatus.NotConfigured;
                }
            }
            if (!res.ok) {
                return {
                    cosmosDB: false,
                    status: formattedResponse
                };
            } else {
                return {
                    cosmosDB: true,
                    status: formattedResponse
                };
            }
        })
        .catch(err => {
            console.error("There was an issue fetching your assistants.");
            return {
                cosmosDB: false,
                status: err
            };
        });
    return response;
};

export const assistantsList = async (): Promise<Assistant[] | null> => {
    const response = await fetch("/assistants/list", {
        method: "GET",
        headers: getHeaders()
    })
        .then(async res => {
            const payload = await res.json();
            if (!Array.isArray(payload)) {
                console.error("There was an issue fetching your assistants.");
                return null;
            }
            const assistants: Assistant[] = await Promise.all(
                payload.map(async (assist: any) => {
                    const assistant: Assistant = {
                        assistant: assist.assistant,
                        title: assist.title,
                        examples: assist.examples
                    };
                    return assistant;
                })
            );
            return assistants;
        })
        .catch(err => {
            console.error("There was an issue fetching your assistants.");
            return null;
        });

    return response;
};

export async function updateFeedback(request: FeedbackRequest): Promise<{ convId: string; message: ChatMessage } | null> {
    const response = await fetch(`${BACKEND_URI}/feedback/update`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify(request)
    })
        .then(async res => {
            if (!res) {
                console.error("There was an issue submitting your feedback.");
                return null;
            }
            const payload = await res.json();
            const message: { convId: string; message: ChatMessage } = {
                convId: payload.conversation_id,
                message: {
                    id: payload.chat_message.id,
                    message: payload.chat_message.message,
                    context: payload.chat_message.context,
                    date: payload.chat_message.createdAt,
                    feedback: payload.chat_message.feedback,
                    feedback_comment: payload.chat_message.feedbackComment
                }
            };
            return message;
        })
        .catch(err => {
            console.error("There was an issue submitting your feedback.");
            return null;
        });
    return response;
}

export async function verifyRecaptcha(token: string): Promise<{ message: string, token: string } | { error: string } | null> {
    const response = await fetch(`${BACKEND_URI}/api/verify`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            token: token
        })
    })
        .then(async res => {
            if (!res.ok) {
                console.error("There was an issue verifying the reCAPTCHA token.");
                return null;
            }
            const payload = await res.json();
            return payload;
        })
        .catch(err => {
            console.error("There was an issue verifying the reCAPTCHA token.", err);
            return null;
        });

    return response;
}
